import { Injectable } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { map, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
  GetPowerOlxItemsParams,
  OlxAdvertActionPayload,
  OlxAdvertCommand,
  OlxAdvertFullStatistics,
  OlxAdvertItem,
  OlxAdvertStatisticName,
  OlxAvailablePackage,
  OlxBalance,
  OlxCategory,
  OlxCategoryAttribute,
  OlxDuplicateAdvertPayload,
  OlxItem,
  OlxLocation,
  OlxLocationZone,
  OlxPaidFeatureCode,
  PowerOlxActionResult,
  PowerOlxV2Response,
  BulkChangePricePayload,
  BulkChangePriceResult,
} from '../interfaces/power-olx-v2.interface';
import { IResponseWithData } from '../../../../interfaces';
import { IParams } from '../../../../model/http';
import {
  BuyAdsBundlesPayload,
  OlxAdsBundleData,
} from '../../../../components/layout/stacked-sidebar/components/buy-olx-ads-bundles/buy-olx-ads-bundles.interface';
import { CardItemState } from '../interfaces/power-olx-tab-item.interface';
import { ERROR_MESSAGES_STRING } from '../../../../enum/constants';
import { PowerOlxStateService } from './power-olx-state.service';
import { NotificationService } from '../../../../services/notifications.service';
import { TranslateService } from '@ngx-translate/core';
import { createQueryParams } from '../../../../helpers';

@Injectable({
  providedIn: 'root',
})
export class PowerOlxV2Service {
  constructor(
    private readonly http: HttpClient,
    private readonly state: PowerOlxStateService,
    private readonly notificationService: NotificationService,
    private readonly translate: TranslateService,
  ) {
  }

  getBalance(channelId: string): Observable<OlxBalance> {
    return this.http.get<IResponseWithData<OlxBalance>>(
      `${environment.baseURL}/olx-tools-v2/balance`,
      { params: { channel_id: channelId } },
    ).pipe(map(v => v.data));
  }

  getItemsByTab(params: GetPowerOlxItemsParams): Observable<PowerOlxV2Response<OlxItem>> {
    const httpParams = createQueryParams(params);

    return this.http.get<IResponseWithData<PowerOlxV2Response<OlxItem>>>(
      `${environment.baseURL}/olx-tools-v2/items`,
      { params: httpParams },
    ).pipe(map(v => v.data));
  }

  getAdvert(channelId: string, advertId: number): Observable<OlxAdvertItem> {
    return this.http.get<IResponseWithData<OlxAdvertItem>>(
      `${environment.baseURL}/olx-tools-advert/${channelId}/advert/${advertId}`,
    ).pipe(map(v => v.data));
  }

  getAdvertFullStatistics(channelId: string, advertId: number): Observable<OlxAdvertFullStatistics> {
    return this.http.get<IResponseWithData<OlxAdvertFullStatistics>>(
      `${environment.baseURL}/olx-tools-advert/${channelId}/advert/${advertId}/statistics`,
    ).pipe(map(v => v.data));
  }

  createAdvert(channelId: string, payload: OlxAdvertActionPayload): Observable<void> {
    return this.http.post<IResponseWithData<void>>(
      `${environment.baseURL}/olx-tools-advert/${channelId}/advert`,
      payload,
    ).pipe(map(v => v.data));
  }

  updateAdvert(channelId: string, advertId: number, payload: OlxAdvertActionPayload): Observable<void> {
    return this.http.patch<IResponseWithData<void>>(
      `${environment.baseURL}/olx-tools-advert/${channelId}/advert/${advertId}`,
      payload,
    ).pipe(map(v => v.data));
  }

  changePriceBulk(channelId: string, payload: BulkChangePricePayload): Observable<BulkChangePriceResult[]> {
    return this.http.patch<IResponseWithData<BulkChangePriceResult[]>>(
      `${environment.baseURL}/olx-tools-advert/${channelId}/advert/bulk/change-price`,
      payload,
    ).pipe(map(v => v.data));
  }

  duplicateAdverts(channelId: string, payload: OlxDuplicateAdvertPayload): Observable<PowerOlxActionResult[]> {
    return this.http.post<IResponseWithData<PowerOlxActionResult[]>>(
      `${environment.baseURL}/olx-tools-advert/${channelId}/adverts/duplicate`,
      payload,
    ).pipe(map(v => v.data));
  }

  commandsAdverts(channelId: string, advertIds: number[], command: OlxAdvertCommand): Observable<PowerOlxActionResult[]> {
    return this.http.post<IResponseWithData<PowerOlxActionResult[]>>(
      `${environment.baseURL}/olx-tools-advert/${channelId}/adverts/command`,
      {
        advert_ids: advertIds,
        command,
      },
    ).pipe(map(v => v.data));
  }

  deleteAdverts(channelId: string, advertIds: number[]): Observable<PowerOlxActionResult[]> {
    return this.http.post<IResponseWithData<PowerOlxActionResult[]>>(
      `${environment.baseURL}/olx-tools-advert/${channelId}/adverts/delete`,
      {
        advert_ids: advertIds,
      },
    ).pipe(map(v => v.data));
  }

  purchasePaidFeatures(channelId: string, advertIds: number[], code: OlxPaidFeatureCode): Observable<PowerOlxActionResult[]> {
    return this.http.post<IResponseWithData<PowerOlxActionResult[]>>(
      `${environment.baseURL}/olx-tools-advert/${channelId}/adverts/paid-features`,
      {
        advert_ids: advertIds,
        code,
      },
    ).pipe(map(v => v.data));
  }

  clearAdvertsStatistics(
    channelId: string,
    advertIds: number[],
    statisticName: OlxAdvertStatisticName,
  ): Observable<PowerOlxActionResult[]> {
    return this.http.post<IResponseWithData<PowerOlxActionResult[]>>(
      `${environment.baseURL}/olx-tools-advert/${channelId}/adverts/clear-statistics`,
      {
        advert_ids: advertIds,
        statistic_name: statisticName,
      },
    ).pipe(map(v => v.data));
  }

  restoreAdverts(channelId: string, backupsIds: string[]): Observable<PowerOlxActionResult[]> {
    return this.http.post<IResponseWithData<PowerOlxActionResult[]>>(
      `${environment.baseURL}/olx-tools-backup/${channelId}/adverts/restore`,
      { backups_ids: backupsIds },
    ).pipe(map(v => v.data));
  }

  getAdsBundles(channelId: string, advertIds: number[]): Observable<OlxAdsBundleData> {
    const httpParams = createQueryParams({
      advert_ids: advertIds,
    });

    return this.http.get<IResponseWithData<OlxAdsBundleData>>(
      `${environment.baseURL}/olx-tools-advert/${channelId}/adverts/ads-bundles`,
      { params: httpParams },
    ).pipe(map(v => v.data));
  }

  buyAdsBundles(channelId: string, payload: BuyAdsBundlesPayload): Observable<PowerOlxActionResult[]> {
    return this.http.post<IResponseWithData<PowerOlxActionResult[]>>(
      `${environment.baseURL}/olx-tools-advert/${channelId}/adverts/ads-bundles`,
      payload,
    ).pipe(map(v => v.data));
  }

  getCities(params: IParams): Observable<OlxLocation[]> {
    const httpParams = createQueryParams(params);

    return this.http.get<IResponseWithData<OlxLocation[]>>(
      `${environment.baseURL}/olx-tools-v2/cities`,
      { params: httpParams },
    ).pipe(map(v => v.data));
  }

  getCategories(): Observable<OlxCategory[]> {
    return this.http.get<IResponseWithData<OlxCategory[]>>(
      `${environment.baseURL}/olx-tools-v2/categories`,
    ).pipe(map(v => v.data));
  }

  getCategoryAttributes(categoryId: number): Observable<OlxCategoryAttribute[]> {
    return this.http.get<IResponseWithData<OlxCategoryAttribute[]>>(
      `${environment.baseURL}/olx-tools-v2/categories/${categoryId}/attributes`,
    ).pipe(map(v => v.data));
  }

  getAvailableLocationZones(channelId: string, categoryId: number): Observable<OlxLocationZone[]> {
    return this.http.get<IResponseWithData<OlxLocationZone[]>>(
      `${environment.baseURL}/olx-tools-v2/zones/${categoryId}`,
      { params: { channel_id: channelId } },
    ).pipe(map(v => v.data));
  }

  getAvailablePackages(channelId: string, categoryId: number, zoneId: string | null): Observable<OlxAvailablePackage[]> {
    return this.http.get<IResponseWithData<OlxAvailablePackage[]>>(
      `${environment.baseURL}/olx-tools-v2/packages/${categoryId}`,
      {
        params: {
          channel_id: channelId,
          ...(zoneId && { zone_id: zoneId })
        }
      },
    ).pipe(map(v => v.data));
  }

  purchasePackage(channelId: string, selectedPackage: OlxAvailablePackage): Observable<void> {
    return this.http.post<IResponseWithData<void>>(
      `${environment.baseURL}/olx-tools-v2/packages/`,
      {
        selected_package: selectedPackage,
      },
      {
        params: {
          channel_id: channelId,
        }
      },
    ).pipe(map(v => v.data));
  }

  showResultsOnActions(
    results: PowerOlxActionResult[],
    state: CardItemState,
    defaultErrorMessage: string,
    defaultWarningMessage?: string,
  ): void {
    results.forEach(r => {
      if (r.error) {
        state = CardItemState.Error;
      } else if (r.warning) {
        state = CardItemState.Warning;
      }

      this.state.updateCardItemState(r.id, state);
    });

    const someError = results.find(v => v.error);
    const someWarning = results.find(v => v.warning);
    if (someError) {
      const errorMessage = ERROR_MESSAGES_STRING[someError.error || ''] || defaultErrorMessage;
      this.notificationService.showError(
        this.translate.instant('notifications_texts.error.error'),
        this.translate.instant(errorMessage),
      );
    } else if (someWarning) {
      const warningMessage = ERROR_MESSAGES_STRING[someWarning.warning || ''] || defaultWarningMessage!;
      this.notificationService.showWarning(
        this.translate.instant('notifications_texts.warn.warn'),
        this.translate.instant(warningMessage),
      );
    }
  }
}
